import httpService from "./httpService";
import type {
  CommunicationEndpoint,
  CommunicationEndpointsResponse,
  EndpointVerificationConfirmResponse,
  EndpointVerificationRequestResponse,
  NotificationCommunicationEndpointType,
  NotificationPreferencePatch,
  NotificationPreferencesResponse,
} from "../types/notifications";

/**
 * Signed-in user's notification settings (PWA-2689), wrapping the gateway
 * routes under `/v3/accounts/...` (plutus-backend
 * `routes/v3/notifications/accountNotificationSettingsRoutes.ts`).
 *
 * - Every call is actor-scoped; delegated account context never changes
 *   whose settings are read or written.
 * - Bodies are built field-by-field (every input schema is
 *   `additionalProperties: false`); `subcategoryKey` is omitted when undefined.
 * - The `sms` field of the endpoints response is rendered only by the SMS card,
 *   which ships hidden (owner decision 2026-09-09; PWA-2698 keeps it behind
 *   `SMS_CONTACT_EDITING_ENABLED` plus the backend's `verification.sms.required`).
 * - Verification (PWA-2698): `requestVerification` / `confirmVerification` return
 *   200 outcomes rather than error statuses; the endpoint type is lower-cased
 *   into the path here so components never build URLs.
 */

const ACTOR_AUTH = { requiresAuth: true, requestScope: "actor" as const };
const PREFERENCES = "/v3/accounts/notification-preferences";
const ENDPOINTS = "/v3/accounts/communication-endpoints";

const toPatchBody = (patch: NotificationPreferencePatch): NotificationPreferencePatch => {
  const entry: NotificationPreferencePatch = {
    categoryKey: patch.categoryKey,
    channel: patch.channel,
    enabled: patch.enabled,
  };
  if (patch.subcategoryKey !== undefined) entry.subcategoryKey = patch.subcategoryKey;
  return entry;
};

class NotificationSettingsService {
  async getPreferences(): Promise<NotificationPreferencesResponse> {
    const response = await httpService.get<NotificationPreferencesResponse>(PREFERENCES, ACTOR_AUTH);
    return response.data;
  }

  async updatePreferences(patch: NotificationPreferencePatch[]): Promise<NotificationPreferencesResponse> {
    const response = await httpService.put<NotificationPreferencesResponse>(
      PREFERENCES,
      { preferences: patch.map(toPatchBody) },
      ACTOR_AUTH
    );
    return response.data;
  }

  async getEndpoints(): Promise<CommunicationEndpointsResponse> {
    const response = await httpService.get<CommunicationEndpointsResponse>(ENDPOINTS, ACTOR_AUTH);
    return response.data;
  }

  async replaceEmail(email: string): Promise<CommunicationEndpoint> {
    const response = await httpService.put<CommunicationEndpoint>(`${ENDPOINTS}/email`, { email: email.trim() }, ACTOR_AUTH);
    return response.data;
  }

  /** Self-service SMS replacement; 409 (conflict) until SMS verification is enabled on the backend. */
  async replaceSms(phoneNumber: string): Promise<CommunicationEndpoint> {
    const response = await httpService.put<CommunicationEndpoint>(
      `${ENDPOINTS}/sms`,
      { phoneNumber: phoneNumber.trim() },
      ACTOR_AUTH
    );
    return response.data;
  }

  async removeSms(): Promise<void> {
    await httpService.delete<unknown>(`${ENDPOINTS}/sms`, ACTOR_AUTH);
  }

  /** Sends a code to the destination currently stored for the actor; no body. */
  async requestVerification(endpointType: NotificationCommunicationEndpointType): Promise<EndpointVerificationRequestResponse> {
    const response = await httpService.post<EndpointVerificationRequestResponse>(
      `${ENDPOINTS}/${endpointType.toLowerCase()}/verification`,
      undefined,
      ACTOR_AUTH
    );
    return response.data;
  }

  async confirmVerification(
    endpointType: NotificationCommunicationEndpointType,
    code: string
  ): Promise<EndpointVerificationConfirmResponse> {
    const response = await httpService.post<EndpointVerificationConfirmResponse>(
      `${ENDPOINTS}/${endpointType.toLowerCase()}/verification/confirm`,
      { code: code.trim() },
      ACTOR_AUTH
    );
    return response.data;
  }
}

export const notificationSettingsService = new NotificationSettingsService();
export default notificationSettingsService;
